import type { ReactNode } from "react";
import { applicationTypeLabels } from "@common/types/closeApplicationType";
import { CloseHealth } from "@common/types/closeHealth";
import {
    ClientsWorkOverviewColumn,
    DEFAULT_ESTIMATED_TIME_CELL_VARIANT,
    EstimatedTimeCellVariantSchema,
    TaskProgressCellVariantSchema,
} from "@common/types/views/clientsWorkOverviewView";
import type { ColumnOptions } from "@common/types/views/views";
import { formatCloseTemplateDateRange } from "@components/settings/OrganizationCloseTemplates/formatCloseTemplateDateRange";
import type { BoardCardField } from "@design-system/Board/Board.types";
import type { ColumnSettingsConfig } from "@design-system/ViewToolbar/ColumnSettingsList";
import { resolveColumnVariant } from "@design-system/ViewTable/core/resolveColumnVariant";
import { CircularTaskProgress } from "../cells/CircularTaskProgress";
import { formatWorkDate } from "../cells/WorkCloseDateCell";
import { WorkHealth } from "../cells/WorkHealthCell";
import { getMyRoles } from "../cells/WorkMyRoleCell";
import { WorkEstimatedTime } from "../cells/WorkEstimatedTimeCell";
import {
    formatWorkTaskProgress,
    getWorkTaskProgressPercentage,
} from "../cells/WorkTaskProgressCell";
import type { ClientsWorkOverviewWorkRow } from "../clientsWorkOverviewViewConfig";
import { daysInStage } from "../workRowCompare";
import { workPageViewConfig } from "../workPageViewConfig";
import {
    WorkCardAppliedBy,
    WorkCardDeliveryDate,
    getWorkCardFieldName,
    getWorkCardStageTag,
    WorkCardTag,
    workCardDateTag,
    workCardFieldNameTag,
} from "./WorkCardTag";
import {
    CARD_FIELD_COLUMNS,
    isProgressSwappedForHealth,
    type WorkCardFieldColumn,
    type WorkCardFieldId,
} from "./workCardFieldColumns";

/** The List columns a card can show, as the gear's card fields list them. */
const WORK_CARD_FIELD_CONFIGS = Object.fromEntries(
    CARD_FIELD_COLUMNS.map((id) => [id, workPageViewConfig.columns[id]]),
);

/**
 * The gear's card fields, given the fields cards show.
 * While cards show Health in Progress's place, Progress's row says so.
 */
export const getWorkCardFieldConfigs = (
    shownFieldIds: readonly string[],
): Readonly<Record<string, ColumnSettingsConfig>> => {
    const progress = ClientsWorkOverviewColumn.TASK_PROGRESS;
    return isProgressSwappedForHealth(shownFieldIds)
        ? {
              ...WORK_CARD_FIELD_CONFIGS,
              [progress]: {
                  ...WORK_CARD_FIELD_CONFIGS[progress],
                  hint: "Shows Health when work has a health status",
              },
          }
        : WORK_CARD_FIELD_CONFIGS;
};

const dateTag = (id: WorkCardFieldId, date: string | null) =>
    date && workCardDateTag(id, formatWorkDate(date));

interface FieldContext {
    options: ColumnOptions | null | undefined;
    /** The signed-in user's id, for My role. */
    userId: number | undefined;
}

type FieldContent = (
    row: ClientsWorkOverviewWorkRow,
    context: FieldContext,
) => ReactNode;

const getMyRoleContent: FieldContent = (row, { userId }) => {
    const myRoles = getMyRoles({ row, userId });
    return (
        myRoles !== "" &&
        workCardFieldNameTag(ClientsWorkOverviewColumn.MY_ROLE, myRoles)
    );
};

const getDaysInStageContent: FieldContent = (row) => {
    const days = daysInStage(row);
    return (
        days !== null &&
        workCardFieldNameTag(
            ClientsWorkOverviewColumn.TIME_IN_STAGE,
            `${days} d`,
        )
    );
};

/** Progress as text, or the circle alone with the count and percentage on hover. */
const getTaskProgressContent: FieldContent = (row, { options }) => {
    if (row.totalTaskCount === 0) {
        return null;
    }
    const id = ClientsWorkOverviewColumn.TASK_PROGRESS;
    const variant = resolveColumnVariant({
        meta: options ?? undefined,
        schema: TaskProgressCellVariantSchema,
        fallback: "count",
    });
    const progress = formatWorkTaskProgress({ row, variant });
    if (variant !== "circle") {
        return workCardFieldNameTag(id, progress);
    }
    return (
        <WorkCardTag
            id={id}
            value={progress}
            tooltip="fieldAndValue"
            icon={
                <CircularTaskProgress
                    percentage={getWorkTaskProgressPercentage(row)}
                />
            }
        />
    );
};

/** The time summary; its breakdown tooltip names the field, so the tag adds none. */
const getEstimatedTimeContent: FieldContent = (row, { options }) =>
    (row.estimatedTimeRemaining > 0 || row.estimatedTimeDone > 0) && (
        <WorkCardTag
            id={ClientsWorkOverviewColumn.ESTIMATED_TIME}
            tooltip="none"
        >
            <WorkEstimatedTime
                row={row}
                variant={resolveColumnVariant({
                    meta: options ?? undefined,
                    schema: EstimatedTimeCellVariantSchema,
                    fallback: DEFAULT_ESTIMATED_TIME_CELL_VARIANT,
                })}
                title={getWorkCardFieldName(
                    ClientsWorkOverviewColumn.ESTIMATED_TIME,
                )}
            />
        </WorkCardTag>
    );

/** Each tag's content, as its List cell shows it; nothing when the cell is empty. */
const FIELD_CONTENT: Record<WorkCardFieldId, FieldContent> = {
    [ClientsWorkOverviewColumn.CLOSE_START]: (row) =>
        dateTag(ClientsWorkOverviewColumn.CLOSE_START, row.startDate),
    [ClientsWorkOverviewColumn.CLOSE_END]: (row) =>
        dateTag(ClientsWorkOverviewColumn.CLOSE_END, row.endDate),
    [ClientsWorkOverviewColumn.SERVICE_PERIOD]: (row) =>
        (row.startDate ?? row.endDate) &&
        workCardDateTag(
            ClientsWorkOverviewColumn.SERVICE_PERIOD,
            formatCloseTemplateDateRange(row.startDate, row.endDate),
        ),
    [ClientsWorkOverviewColumn.WORK_START_DATE]: (row) =>
        dateTag(ClientsWorkOverviewColumn.WORK_START_DATE, row.workStartDate),
    [ClientsWorkOverviewColumn.WORK_DELIVERY_DATE]: (row) =>
        row.workDeliveryDate && <WorkCardDeliveryDate row={row} />,
    [ClientsWorkOverviewColumn.CLOSE_STAGE]: getWorkCardStageTag,
    [ClientsWorkOverviewColumn.HEALTH]: (row) =>
        row.health !== CloseHealth.NONE && <WorkHealth row={row} size="sm" />,
    [ClientsWorkOverviewColumn.MY_ROLE]: getMyRoleContent,
    [ClientsWorkOverviewColumn.TIME_IN_STAGE]: getDaysInStageContent,
    [ClientsWorkOverviewColumn.TASK_PROGRESS]: getTaskProgressContent,
    [ClientsWorkOverviewColumn.ESTIMATED_TIME]: getEstimatedTimeContent,
    [ClientsWorkOverviewColumn.APPLICATION_TYPE]: (row) =>
        workCardFieldNameTag(
            ClientsWorkOverviewColumn.APPLICATION_TYPE,
            applicationTypeLabels[row.applicationType],
        ),
    [ClientsWorkOverviewColumn.APPLIED_BY]: (row) =>
        row.createdByUserId !== null && (
            <WorkCardAppliedBy userId={row.createdByUserId} />
        ),
    [ClientsWorkOverviewColumn.APPLIED_AT]: (row) =>
        dateTag(ClientsWorkOverviewColumn.APPLIED_AT, row.createdAt),
    [ClientsWorkOverviewColumn.UNREAD_TASKS]: (row) =>
        row.unreadTaskCount > 0 &&
        workCardFieldNameTag(
            ClientsWorkOverviewColumn.UNREAD_TASKS,
            row.unreadTaskCount,
        ),
};

/** The card's inline tag fields, in `fieldColumns`' order. Empty cells hide their tag. */
export const getWorkCardTagFields = ({
    row,
    fieldColumns,
    userId,
}: {
    row: ClientsWorkOverviewWorkRow;
    fieldColumns: readonly WorkCardFieldColumn[];
    userId: number | undefined;
}): BoardCardField[] =>
    fieldColumns.map((column) => ({
        id: column.id,
        placement: "inline",
        label: getWorkCardFieldName(column.id),
        content: FIELD_CONTENT[column.id](row, {
            options: column.options,
            userId,
        }),
    }));
